import React from 'react';
import { LumirAppendixValues } from './types';

export interface ContentLumirAppendixProps {
  mainContractNumber?: string;
  mainContractDate?: string;
  values: LumirAppendixValues;
}

// Helper component for blank underline
const BlankField: React.FC<{ width?: string }> = ({ width = 'w-32' }) => (
  <span className={`inline-block border-b border-gray-900 ${width} h-px ml-1 translate-y-[-2px]`}></span>
);

// Helper to render value or blank field
const renderValue = (value: string | undefined, width: string = 'w-32') => {
  if (value) return value;
  return <BlankField width={width} />;
};

const ContentLumirAppendix: React.FC<ContentLumirAppendixProps> = ({
  mainContractNumber,
  mainContractDate,
  values,
}) => {
  return (
    <div className="!space-y-5 !text-sm">
      {/* ĐIỀU I */}
      <div className="pdf-block">
        <h4 className="!font-bold !text-gray-900 dark:!text-white !mb-2">ĐIỀU I: GÓI ĐẦU TƯ VÀ LOẠI HÌNH ĐẦU TƯ</h4>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p>
            <strong>-</strong> Tên gói đầu tư: {renderValue(values.investment.packageName, 'w-40')}
            &nbsp;&nbsp;&nbsp; Số lượng: {renderValue(values.investment.quantity, 'w-24')}
          </p>
          <p>
            <strong>-</strong> Mức đầu tư: {renderValue(values.investment.investmentAmount, 'w-40')}
          </p>
          <p>
            <strong>-</strong> Loại hình đầu tư: Hợp tác chia lợi nhuận.
          </p>
        </div>
      </div>

      {/* ĐIỀU II */}
      <div className="pdf-block">
        <h4 className="!font-bold !text-gray-900 dark:!text-white !mb-2">ĐIỀU II: KỲ CHIA & CƠ CHẾ CHIA</h4>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p><strong>-</strong> Kỳ chia: Hàng tháng;</p>
          <p><strong>-</strong> Ngày chốt: ngày cuối cùng của tháng;</p>
          <p><strong>-</strong> Ngày chi: ngày 15 hàng tháng.</p>
          <p><strong>-</strong> Mức chia sẻ tạm ứng tối thiểu 3%/ giá trị tham gia/ tháng/ đối tác:</p>
          <div className="!pl-4 !space-y-1">
            <p>
              <strong>o</strong> Nếu Quỹ chia sẻ của đối tác trong kỳ &lt; 3%/ giá trị tham gia ⇒ Bên A tạm ứng phần thiếu để đủ 3%/ giá trị tham gia/ tháng.
            </p>
            <p>
              <strong>o</strong> Khoản chia sẻ tối thiểu được chi trả từ Quỹ cân bằng thu nhập của Dự án LUMIR và CCPI và không sử dụng nguồn vốn hoặc lợi nhuận từ các hoạt động khác của Công ty Cổ phần BEQ Holdings. Phần tạm ứng này được ghi nhận như chi phí tạm thời và sẽ bù trừ vào kỳ doanh thu vượt mức tối thiểu.
            </p>
            <p>
              <strong>o</strong> Phần tạm ứng sẽ bù trừ ở các kỳ sau khi Quỹ chia vượt mức tối thiểu.
            </p>
            <p>
              <strong>o</strong> Nếu hợp đồng chấm dứt mà còn dư tạm ứng, hai bên quyết toán trong vòng 30 ngày làm việc.
            </p>
          </div>
        </div>
      </div>

      {/* ĐIỀU III */}
      <div className="pdf-block">
        <h4 className="!font-bold !text-gray-900 dark:!text-white !mb-2">ĐIỀU III: HÌNH THỨC VÀ PHƯƠNG THỨC CHUYỂN TIỀN</h4>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p><strong>-</strong> Hình thức: chuyển khoản.</p>
          <p>
            <strong>-</strong> Phương thức chuyển tiền: Chuyển khoản 100% giá trị hợp đồng hoặc có thể chia thành 02 đợt như sau:
          </p>
          <div className="!pl-4 !space-y-1">
            <p>
              <strong>+</strong> Đợt 1: Đặt cọc {renderValue(values.payment.deposit, 'w-32')} VND vào ngày {renderValue(values.payment.depositDate, 'w-24')}
            </p>
            <p>
              <strong>+</strong> Đợt 2: Chuyển khoản {renderValue(values.payment.finalPayment, 'w-32')} VND vào ngày {renderValue(values.payment.finalPaymentDate, 'w-24')}
            </p>
          </div>
          <p>
            <strong>-</strong> Thông tin chủ tài khoản: Bên B chuyển tiền cho Bên A theo thông tin tài khoản ghi trong hợp đồng hợp tác.
          </p>
          <p>
            <strong>-</strong> Phí chuyển tiền: bên nào chuyển, bên đó chịu. Thuế TNCN (nếu có): Bên A khấu trừ/ nộp thay theo luật.
          </p>
        </div>
      </div>

      {/* ĐIỀU IV */}
      <div className="pdf-block">
        <h4 className="!font-bold !text-gray-900 dark:!text-white !mb-2">ĐIỀU IV: ĐIỀU KHOẢN CHUNG</h4>
        <div className="!text-gray-700 dark:!text-gray-300 !space-y-2 !pl-4">
          <p>
            <strong>-</strong> Phụ lục này có hiệu lực từ ngày ký cho đến khi hai Bên có thỏa thuận khác;
          </p>
          <p>
            <strong>-</strong> Phụ lục này được lập hai (02) bản, mỗi Bên giữ một (01) bản, có giá trị pháp lý như nhau.
          </p>
          <p>
            <strong>-</strong> Phụ lục là một phần không thể tách rời của Hợp đồng hợp tác số {renderValue(mainContractNumber, 'w-24')} ngày {renderValue(mainContractDate, 'w-20')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContentLumirAppendix;
